import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { Button } from '../../../../ui/components/ui/button';
import { Form } from '../../../../ui/components/ui/form';
import { Error as ErrorI } from '../../../root/context';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OFFER_CONFLICTS } from '../../constants/offer-conflicts';
import { OfferFormPanel } from '../../constants/offer-form-panel.enum';
import { AvailabilityCheckFn } from '../../hooks/use-availability-check';
import { useOfferFormPanels } from '../../hooks/use-offer-form-panels';
import { getResultingOfferFile, KEEP_OFFER_FILE, OfferFileChange, StoredOfferFile } from '../../lib/offer-file';
import { CreateOfferRequestDto } from '../../types/request';
import { OfferFormOptionsResponseDto, OfferMetaResponseDto } from '../../types/response';
import OfferAddOnSection from '../offer-add-on-section/offer-add-on-section';
import OfferCustomerSection from '../offer-customer-section/offer-customer-section';
import OfferFileSection from '../offer-file-section/offer-file-section';
import OfferPaymentSection from '../offer-payment-section/offer-payment-section';
import OfferPreview from '../offer-preview/offer-preview';
import OfferProductSection from '../offer-product-section/offer-product-section';
import SendOfferDialog from '../send-offer-dialog/send-offer-dialog';
import { OFFER_FORM_DEFAULTS, offerFormSchema, OfferFormValues, toCreateOfferRequest } from './offer-form.schema';

export interface OfferFormProps {
  billingTerms: BillingTermMetaResponseDto[];
  meta: OfferMetaResponseDto;
  options: OfferFormOptionsResponseDto;
  /** A saved draft to edit – without it the form starts empty (new offer). */
  initialValues?: OfferFormValues;
  /** The PDF stored with the draft. */
  storedOfferFile?: StoredOfferFile | null;
  onCancel: () => void;
  /** Saves the draft (and its PDF) without sending it. Resolves `null` on success, otherwise the error of the request. */
  onSaveDraft: (payload: CreateOfferRequestDto, offerFile: OfferFileChange) => Promise<ErrorI | null>;
  /** Saves and sends it. Resolves like `onSaveDraft` – a save error is returned, everything after that is the caller's. */
  onSend: (payload: CreateOfferRequestDto, offerFile: OfferFileChange, validityDays: number) => Promise<ErrorI | null>;
  /** Live checks of mail and company name while typing. */
  checkMailAvailability?: AvailabilityCheckFn;
  checkCompanyNameAvailability?: AvailabilityCheckFn;
}

/** Form on the left, live preview of the checkout on the right. */
const OfferForm = ({
  billingTerms,
  meta,
  options,
  initialValues,
  storedOfferFile = null,
  onCancel,
  onSaveDraft,
  onSend,
  checkMailAvailability,
  checkCompanyNameAvailability
}: OfferFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The validated payload waiting for the send dialog.
  const [pendingSend, setPendingSend] = useState<CreateOfferRequestDto | null>(null);
  const [offerFileChange, setOfferFileChange] = useState<OfferFileChange>(KEEP_OFFER_FILE);
  const { panelProps, openPanelsFor, handleInvalid } = useOfferFormPanels();

  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    defaultValues: initialValues ?? OFFER_FORM_DEFAULTS
  });

  const showConflict = (error: ErrorI | null) => {
    const conflict = error?.entity ? OFFER_CONFLICTS[error.entity] : undefined;
    if (!conflict) return;
    // Rendered first – a collapsed panel has no input to focus.
    flushSync(() => openPanelsFor([OfferFormPanel.CUSTOMER]));
    form.setError(conflict.field, { message: conflict.message }, { shouldFocus: true });
  };

  const handleSaveDraft = async (values: OfferFormValues) => {
    setIsSubmitting(true);
    try {
      showConflict(await onSaveDraft(toCreateOfferRequest(values), offerFileChange));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSend = async (validityDays: number): Promise<boolean> => {
    if (!pendingSend) return true;
    setIsSubmitting(true);
    try {
      const error = await onSend(pendingSend, offerFileChange, validityDays);
      // Closed first – the dialog hands the focus back to its button when it closes, the conflict field takes it after that.
      flushSync(() => setPendingSend(null));
      showConflict(error);
      return true;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
        {/* The preview renders a form of its own, so only the left column is the form. */}
        <form noValidate onSubmit={form.handleSubmit(handleSaveDraft, handleInvalid)} className="flex min-w-0 flex-col gap-5">
          <OfferProductSection
            form={form}
            billingTerms={billingTerms}
            products={options.products}
            panel={panelProps(OfferFormPanel.PRODUCT)}
            detailsPanel={panelProps(OfferFormPanel.PRODUCT_DETAILS)}
          />
          <OfferAddOnSection control={form.control} addOns={options.addOns} panel={panelProps(OfferFormPanel.ADD_ONS)} />
          <OfferCustomerSection
            control={form.control}
            panel={panelProps(OfferFormPanel.CUSTOMER)}
            checkMailAvailability={checkMailAvailability}
            checkCompanyNameAvailability={checkCompanyNameAvailability}
          />
          <OfferPaymentSection control={form.control} startDateOptions={meta.startDateOptions} panel={panelProps(OfferFormPanel.PAYMENT)} />
          <OfferFileSection
            stored={storedOfferFile}
            change={offerFileChange}
            onChange={setOfferFileChange}
            panel={panelProps(OfferFormPanel.OFFER_FILE)}
            disabled={isSubmitting}
          />

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Abbrechen
            </Button>
            <Button type="submit" variant="outline" disabled={isSubmitting}>
              Entwurf speichern
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={form.handleSubmit((values) => setPendingSend(toCreateOfferRequest(values)), handleInvalid)}>
              Angebot versenden
            </Button>
          </div>
        </form>

        <SendOfferDialog
          candidate={pendingSend && { mail: pendingSend.customer.mail, isResend: false }}
          defaultValidityDays={meta.defaultValidityDays}
          maxValidityDays={meta.maxValidityDays}
          onClose={() => setPendingSend(null)}
          onConfirm={handleConfirmSend}
        />

        <OfferPreview
          control={form.control}
          options={options}
          billingTerms={billingTerms}
          meta={meta}
          offerFileName={getResultingOfferFile(offerFileChange, storedOfferFile)?.name ?? null}
        />
      </div>
    </Form>
  );
};

export default OfferForm;
